import Image from 'next/image';

import type { MediaSlot as MediaSlotType } from '@selenos/leads';

interface MediaSlotProps {
  slot: MediaSlotType;
  /**
   * Hero / above-the-fold media should set priority to eager-load the
   * image fallback (important for LCP). Section media leaves this off so
   * the image is lazy-loaded.
   */
  priority?: boolean;
  /**
   * Dual-layer behaviour: on devices that support autoplay and where the
   * user has not set prefers-reduced-motion, the video plays. Otherwise
   * the image renders at the same dimensions. Passed className is applied
   * to both layers so the visual footprint stays identical.
   */
  className?: string;
  /**
   * Override the default `100vw` sizes attribute for non-hero usage.
   * Propagated to both the desktop and mobile image variants.
   */
  sizes?: string;
}

/**
 * MediaSlot: canonical way to render a landing-page background media slot
 * (hero, section break, any autoplay ambient video).
 *
 * Rendering:
 * - motion-safe: video plays; image is hidden via `motion-safe:hidden`
 * - motion-reduce: image is visible; video is hidden via `motion-reduce:hidden`
 * - Pre-roll fallback shown by the browser is the video's `poster` attribute
 *
 * Rules enforced here:
 * - Video is muted, looped, playsInline (iOS autoplay requirement)
 * - `priority` eager-loads the image fallback (important for LCP on hero)
 * - Consumer's `sizes` prop propagates to both image variants
 */
export function MediaSlot({
  slot,
  priority,
  className,
  sizes = '100vw',
}: MediaSlotProps) {
  const { image, video } = slot;
  const sharedClass = className ?? '';

  return (
    <>
      <Image
        src={image.src}
        alt={image.alt}
        fill
        sizes={sizes}
        priority={priority}
        loading={priority ? undefined : 'lazy'}
        className={`${sharedClass} motion-reduce:block ${video ? 'motion-safe:hidden' : ''}`.trim()}
      />
      {image.mobile && (
        <Image
          src={image.mobile}
          alt={image.alt}
          fill
          sizes={sizes}
          priority={priority}
          loading={priority ? undefined : 'lazy'}
          className={`${sharedClass} motion-reduce:block sm:hidden ${video ? 'motion-safe:hidden' : ''}`.trim()}
        />
      )}
      {video && (
        <video
          className={`${sharedClass} motion-reduce:hidden`.trim()}
          autoPlay
          muted
          loop
          playsInline
          preload={priority ? 'metadata' : 'none'}
          poster={video.poster}
          aria-hidden="true"
        >
          {video.webm && <source src={video.webm} type="video/webm" />}
          <source src={video.mp4} type="video/mp4" />
        </video>
      )}
    </>
  );
}
